import authClient from './authClient';
import { redirect } from '@tanstack/react-router';
import { allResources, createCache } from 'cross-state';

export class SessionError extends Error {
    constructor(
        public code: string,
        message: string,
    ) {
        super(message);
    }
}

const authService = new (class {
    appLogin = createCache(
        async () => {
            const session = await authClient.getSession();
            if (session.error) {
                throw new SessionError(
                    session.error.code ?? `HTTP_${session.error.status}`,
                    session.error.message ?? session.error.statusText,
                );
            }

            if (!session.data) {
                return null;
            }

            return {
                user: session.data.appUser,
                session: session.data.session,
                maintenance: session.data.maintenance,
                maintenanceExempt: session.data.maintenanceExempt,
            };
        },
        {
            invalidateOnWindowFocus: true,
        },
    );

    userLanguage = createCache<string>(() => async ({ use }) => {
        // The login page shows session errors, so it still needs a language.
        const appLogin = await use(this.appLogin).catch(() => null);
        return (
            appLogin?.user.settings?.language ??
            appLogin?.user.preferredLanguage?.slice(0, 2) ??
            navigator.language.slice(0, 2)
        );
    });

    /** For route guards: the current login, or a redirect to the login page. */
    async requireLogin(redirectTo: string | undefined) {
        const login = await this.appLogin.get().catch((error: unknown) => {
            throw redirect({
                to: '/login',
                search: {
                    redirect: redirectTo,
                    error: error instanceof SessionError ? error.code : 'UNKNOWN',
                    error_description: error instanceof Error ? error.message : undefined,
                },
            });
        });

        if (!login) {
            throw redirect({ to: '/login', search: { redirect: redirectTo } });
        }

        return login;
    }

    async login(callbackURL = '/') {
        await authClient.signIn.social({
            provider: 'microsoft',
            callbackURL,
            // better-auth appends `error` and `error_description`.
            errorCallbackURL: `/login?redirect=${encodeURIComponent(callbackURL)}`,
        });
    }

    async logout() {
        await authClient.signOut();
        allResources.clearAll();
        window.location.href = '/login';
    }
})();

(window as any).authService = authService;
export default authService;
